// src/components/FilterBar.jsx
import React from "react";
import styled from "styled-components";
import { FiSearch, FiX } from "react-icons/fi";

/**
 * Search + category + sort. Controlled by the parent so both the homepage
 * section and the full products page can share one filter UI.
 */
export default function FilterBar({
  search,
  onSearch,
  categories = [],
  categoryId,
  onCategory,
  sort,
  onSort,
  resultCount,
  showSort = true,
}) {
  return (
    <Wrap>
      <SearchField>
        <FiSearch />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Kërko produkt ose kod"
          aria-label="Kërko produkte"
        />
        {search && (
          <Clear onClick={() => onSearch("")} aria-label="Pastro kërkimin">
            <FiX />
          </Clear>
        )}
      </SearchField>

      <Chips role="group" aria-label="Filtro sipas kategorisë">
        <Chip $active={!categoryId} onClick={() => onCategory("")}>
          Të gjitha
        </Chip>
        {categories.map((c) => (
          <Chip
            key={c.id}
            $active={categoryId === c.id}
            onClick={() => onCategory(c.id)}
          >
            {c.nameSq}
          </Chip>
        ))}
      </Chips>

      <Meta>
        {typeof resultCount === "number" && (
          <Count>
            {resultCount} {resultCount === 1 ? "produkt" : "produkte"}
          </Count>
        )}

        {showSort && (
          <Select
            value={sort}
            onChange={(e) => onSort(e.target.value)}
            aria-label="Rendit produktet"
          >
            <option value="code">Sipas kodit</option>
            <option value="name">Sipas emrit</option>
            <option value="price-asc">Çmimi: nga më i ulëti</option>
            <option value="price-desc">Çmimi: nga më i larti</option>
          </Select>
        )}
      </Meta>
    </Wrap>
  );
}

/**
 * Shared filtering + sorting so the homepage and products page
 * can never drift apart in behaviour.
 */
export const applyFilters = (products, { search, categoryId, sort }) => {
  const q = search.trim().toLowerCase();

  let list = products.filter((p) => {
    if (categoryId && p.categoryId !== categoryId) return false;
    if (!q) return true;
    return [p.nameSq, p.nameDe, p.pmuCode, p.mbCode, p.codeNumber]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  const by = {
    // uncoded products sort to the end rather than clustering at the top
    code: (a, b) => {
      const ac = String(a.codeNumber || "");
      const bc = String(b.codeNumber || "");
      if (!ac && !bc)
        return String(a.nameSq || a.nameDe || "").localeCompare(
          String(b.nameSq || b.nameDe || ""),
          "sq",
        );
      if (!ac) return 1;
      if (!bc) return -1;
      return ac.localeCompare(bc, "sq", { numeric: true });
    },
    name: (a, b) => String(a.nameSq).localeCompare(String(b.nameSq), "sq"),
    "price-asc": (a, b) => a.price - b.price,
    "price-desc": (a, b) => b.price - a.price,
  };

  return list.sort(by[sort] || by.code);
};

/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 380px) 1fr auto;
  align-items: center;
  gap: 20px 28px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg:first-child {
    position: absolute;
    left: 16px;
    color: ${({ theme }) => theme.color.muted};
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 48px;
    padding: 0 42px 0 44px;
    border: 1px solid ${({ theme }) => theme.color.line};
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme }) => theme.color.bg};
    font-size: ${({ theme }) => theme.size.sm};
    transition:
      border-color 0.25s,
      box-shadow 0.25s;

    &::placeholder {
      color: ${({ theme }) => theme.color.muted};
    }
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.color.brand};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.color.brandSoft};
    }
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
`;

const Clear = styled.button`
  position: absolute;
  right: 14px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.color.muted};

  &:hover {
    color: ${({ theme }) => theme.color.ink};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${({ theme }) => theme.down.lg} {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Chip = styled.button`
  flex-shrink: 0;
  padding: 9px 18px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.color.ink : theme.color.line)};
  background: ${({ $active, theme }) =>
    $active ? theme.color.ink : "transparent"};
  color: ${({ $active, theme }) => ($active ? "#fff" : theme.color.inkSoft)};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.medium};
  white-space: nowrap;
  transition: all 0.25s ${({ theme }) => theme.ease.out};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-self: end;

  ${({ theme }) => theme.down.lg} {
    justify-self: stretch;
    justify-content: space-between;
  }
`;

const Count = styled.span`
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  white-space: nowrap;
`;

const Select = styled.select`
  height: 42px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.color.line};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.bg};
  font-size: ${({ theme }) => theme.size.xs};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.brand};
  }
`;
